import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';

export interface LifecycleVariantSummary {
  variant_key: string;
  enrolled: number;
  sent: number;
  send_skipped: number;
  email_clicked: number;
  modal_viewed: number;
  add_to_cart: number;
  purchase_completed: number;
  cancel_requested_14d: number;
  cancel_requested_28d: number;
  sent_rate: number;
  purchase_rate: number;
  cancel_requested_14d_rate: number;
  cancel_requested_28d_rate: number;
}

export interface LifecycleCampaignCard {
  campaign_key: string;
  experiment_key: string | null;
  delivery_provider: string;
  delivery_mode: string;
  window_days: { min: number; max: number | null };
  source_product_ids: string[] | null;
  require_renewed_source: boolean;
  automation_ids: string[];
  kind: string | null;
  enabled_effective: boolean;
  global_flag: boolean;
  db_state: boolean | null;
  env_allowlisted: boolean;
  enrolled_total: number;
  enrolled_by_variant: Record<string, number>;
  last_enrollment_at: string | null;
  summary: { campaign_key: string; variants: LifecycleVariantSummary[] };
}

export interface LifecycleEnrollmentRow {
  id: string;
  patient_id: string;
  email: string;
  variant_key: string | null;
  source_product_id: string | null;
  recommended_product_id: string | null;
  status: string;
  eligibility_reason: Record<string, unknown> | null;
  enrolled_at: string;
}

export interface LifecycleEventRow {
  event_type: string;
  message_key: string | null;
  patient_id: string | null;
  enrollment_id: string | null;
  email?: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface LifecycleCampaignDetail {
  campaign_key: string;
  config: Record<string, unknown>;
  cta_destination?: string;
  cta_posthog_hint?: string;
  enabled_effective: boolean;
  global_flag: boolean;
  db_state: boolean | null;
  summary: { campaign_key: string; variants: LifecycleVariantSummary[] };
  recent_enrollments: LifecycleEnrollmentRow[];
  recent_events: LifecycleEventRow[];
}

export interface LifecycleDryRunResult {
  campaign_key: string;
  considered: number;
  would_enroll_count: number;
  would_enroll: Array<{
    patient_id: string;
    email: string;
    variant_key?: string | null;
    recommended_product_id?: string | null;
    would_send?: boolean;
  }>;
  skip_reasons: Record<string, number>;
}

export function useLifecycleCampaigns() {
  return useQuery({
    queryKey: ['admin', 'lifecycle', 'campaigns'],
    queryFn: () =>
      apiFetch<{ campaigns: LifecycleCampaignCard[]; generated_at: string }>(
        '/admin/lifecycle/campaigns',
      ),
    staleTime: 30_000,
  });
}

export function useLifecycleCampaignDetail(campaignKey: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'lifecycle', 'campaign', campaignKey],
    queryFn: () =>
      apiFetch<LifecycleCampaignDetail>(
        `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey!)}`,
      ),
    enabled: Boolean(campaignKey),
  });
}

export function useSetLifecycleCampaignState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignKey,
      enabled,
      notes,
    }: {
      campaignKey: string;
      enabled: boolean;
      notes?: string;
    }) =>
      apiFetch<{ campaign_key: string; enabled_effective: boolean }>(
        `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey)}/state`,
        { method: 'POST', body: JSON.stringify({ enabled, notes }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'lifecycle'] });
    },
  });
}

export function useLifecycleDryRun() {
  return useMutation({
    mutationFn: ({ campaignKey, limit = 100 }: { campaignKey: string; limit?: number }) =>
      apiFetch<LifecycleDryRunResult>(
        `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey)}/dry-run`,
        { method: 'POST', body: JSON.stringify({ limit }) },
      ),
  });
}

export interface LifecycleTestEnrollResult {
  mode: 'patient' | 'synthetic';
  email: string;
  ok: boolean;
  status?: string;
  provider_trigger_key?: string;
  fields_written?: string[];
  error?: string | null;
}

export function useLifecycleTestEnroll() {
  return useMutation({
    mutationFn: ({ campaignKey, email }: { campaignKey: string; email: string }) =>
      apiFetch<LifecycleTestEnrollResult>(
        `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey)}/test-enroll`,
        { method: 'POST', body: JSON.stringify({ email }) },
      ),
  });
}

export interface LifecycleEnrollmentsPage {
  enrollments: LifecycleEnrollmentRow[];
  total: number;
  limit: number;
  offset: number;
}

export function useLifecycleEnrollments(
  campaignKey: string | undefined,
  page: number,
  pageSize = 50,
) {
  return useQuery({
    queryKey: ['admin', 'lifecycle', 'enrollments', campaignKey, page, pageSize],
    queryFn: () =>
      apiFetch<LifecycleEnrollmentsPage>(
        `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey!)}/enrollments?limit=${pageSize}&offset=${page * pageSize}`,
      ),
    enabled: Boolean(campaignKey),
  });
}

export async function fetchAllLifecycleEnrollments(
  campaignKey: string,
): Promise<LifecycleEnrollmentRow[]> {
  const all: LifecycleEnrollmentRow[] = [];
  let offset = 0;
  const limit = 1000;
  for (;;) {
    const page = await apiFetch<LifecycleEnrollmentsPage>(
      `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey)}/enrollments?limit=${limit}&offset=${offset}`,
    );
    all.push(...page.enrollments);
    if (all.length >= page.total || page.enrollments.length < limit) break;
    offset += limit;
  }
  return all;
}

export interface LifecycleEventsPage {
  events: LifecycleEventRow[];
  total: number;
  limit: number;
  offset: number;
  event_type: string | null;
}

export function useLifecycleEvents(
  campaignKey: string | undefined,
  page: number,
  eventType: string | null,
  pageSize = 25,
) {
  return useQuery({
    queryKey: ['admin', 'lifecycle', 'events', campaignKey, page, eventType, pageSize],
    queryFn: () =>
      apiFetch<LifecycleEventsPage>(
        `/admin/lifecycle/campaigns/${encodeURIComponent(campaignKey!)}/events?limit=${pageSize}&offset=${page * pageSize}${eventType ? `&event_type=${encodeURIComponent(eventType)}` : ''}`,
      ),
    enabled: Boolean(campaignKey),
  });
}

// ── campaign builder (definitions + agent) ──────────────────────────────────

export interface BuilderDefinitionRow {
  campaign_key: string;
  definition: Record<string, unknown>;
  status: 'draft' | 'published' | 'archived';
  validation: { ok?: boolean; errors?: { field: string; message: string }[] } | null;
  preflight: {
    ok?: boolean;
    checks?: { id: string; status: string; detail: string }[];
    cohort?: Record<string, unknown>;
  } | null;
  updated_by: string | null;
  published_at: string | null;
  updated_at: string;
}

export interface BuilderThreadSummary {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface BuilderThreadDetail {
  id: string;
  title: string | null;
  messages: { id: string; role: string; content_text: string; created_at: string }[];
}

export function useBuilderDefinitions() {
  return useQuery({
    queryKey: ['lifecycle', 'builder', 'definitions'],
    queryFn: () => apiFetch<BuilderDefinitionRow[]>('/admin/lifecycle/builder/definitions'),
    refetchInterval: 30_000,
  });
}

export function useBuilderPreflight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignKey: string) =>
      apiFetch<BuilderDefinitionRow['preflight']>(
        `/admin/lifecycle/builder/definitions/${campaignKey}/preflight`,
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lifecycle', 'builder'] }),
  });
}

export function useBuilderPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignKey: string) =>
      apiFetch<Record<string, unknown>>(
        `/admin/lifecycle/builder/definitions/${campaignKey}/publish`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lifecycle'] });
    },
  });
}

export function useBuilderThreads() {
  return useQuery({
    queryKey: ['lifecycle', 'builder', 'threads'],
    queryFn: () => apiFetch<BuilderThreadSummary[]>('/admin/lifecycle/builder/agent/threads'),
  });
}

export function useBuilderThread(threadId: string | null) {
  return useQuery({
    queryKey: ['lifecycle', 'builder', 'thread', threadId],
    queryFn: () =>
      apiFetch<BuilderThreadDetail>(`/admin/lifecycle/builder/agent/threads/${threadId}`),
    enabled: Boolean(threadId),
  });
}

export function useCreateBuilderThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ thread_id: string }>('/admin/lifecycle/builder/agent/threads', {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lifecycle', 'builder', 'threads'] }),
  });
}

export function useSendBuilderMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, content }: { threadId: string; content: string }) =>
      apiFetch<{ reply: string }>(
        `/admin/lifecycle/builder/agent/threads/${threadId}/messages`,
        { method: 'POST', body: JSON.stringify({ content }) },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['lifecycle', 'builder', 'thread', vars.threadId] });
      qc.invalidateQueries({ queryKey: ['lifecycle', 'builder', 'definitions'] });
    },
  });
}
